"use client";

import type { LatestEarnings } from "@/domain/gems-game/domain";
import { RuleId } from "@/domain/gems-game/domain";
import { trackLegacyEvent } from "@analytics";
import { useGetWalletGems, useGetWalletLatestEarnings } from "@hooks/api";
import { useConnection } from "@hooks/connection";
import type { WalletGems } from "@imx-tokens-ts/gems-game/data-access";
import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VariableGems } from "../VariableGems/VariableGems";

const BASE_GEMS = 3;
const HAS_JUST_EARNED_TIME_WINDOW = 10000;

const utcDateOf = (date: Date): string => {
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd");
};

const canClaimGems = (latestEarnings: LatestEarnings | undefined) => {
  if (!latestEarnings) {
    return false;
  }

  const lastEarned = latestEarnings.lastEarnedForRule(RuleId.DAILY_GEMS_CLAIM);
  if (!lastEarned) {
    return true; // if the user has never earned gems, they can claim
  }

  return utcDateOf(lastEarned) !== utcDateOf(new Date());
};

const hasJustEarnedLatestEarnings = (
  latestEarnings: LatestEarnings | undefined,
) => {
  if (!latestEarnings) {
    return false;
  }

  const lastEarned = latestEarnings.lastEarnedForRule(RuleId.DAILY_GEMS_CLAIM);

  if (!lastEarned) {
    return false;
  }

  const lastEarnedTimestamp = lastEarned.getTime();
  const nowTimestamp = Date.now();

  if (
    Math.abs(nowTimestamp - lastEarnedTimestamp) <= HAS_JUST_EARNED_TIME_WINDOW
  ) {
    return true;
  }

  return false;
};

export function GemsLayout() {
  const { walletAddress, isPassportWallet, disconnect } = useConnection();
  const { latestEarnings, updateEarnings, toggleEarningsRefresh } =
    useGetWalletLatestEarnings(walletAddress ?? undefined);

  const hasJustEarnedRefreshTimeoutRef = useRef<number | undefined>(undefined);
  const [lastRecalculationTime, setLastRecalculationTime] = useState<number>(
    () => Date.now(),
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: we recalculate on lastRecalculationTime
  const [canClaim, hasJustEarned] = useMemo(() => {
    const hasJustEarnedLatest = hasJustEarnedLatestEarnings(latestEarnings);
    if (hasJustEarnedLatest) {
      clearTimeout(hasJustEarnedRefreshTimeoutRef.current);
      hasJustEarnedRefreshTimeoutRef.current = setTimeout(() => {
        setLastRecalculationTime(Date.now());
      }, HAS_JUST_EARNED_TIME_WINDOW);
    }
    return [canClaimGems(latestEarnings), hasJustEarnedLatest];
  }, [latestEarnings, lastRecalculationTime]);

  const { walletGems, mutate } = useGetWalletGems(walletAddress, hasJustEarned);

  const onUTCDayChange = useCallback(() => {
    // ! here we need to refresh gem endpoint as well.
    mutate();
    setLastRecalculationTime(Date.now());
  }, [mutate]);

  const eligibleGems = useMemo(() => {
    return walletGems?.dailyGemsClaimable || BASE_GEMS;
  }, [walletGems]);

  const earningsRefreshTimeoutRef = useRef<number | undefined>(undefined);

  const onGemEarned = useCallback(
    (passedWalletGems: WalletGems) => {
      if (!latestEarnings) {
        throw new Error("latestEarnings is not defined");
      }

      toggleEarningsRefresh(true);

      const newGemBalance = passedWalletGems.gems + eligibleGems;
      // optimistic update to the gem count and do not refetch as the data on the server
      // might not have been updated yet
      const claimTime = new Date();
      mutate(
        {
          ...passedWalletGems,
          gems: newGemBalance,
        },
        {
          revalidate: false,
        },
      );

      const dailyEarning = latestEarnings.getEarning(RuleId.DAILY_GEMS_CLAIM);

      updateEarnings({
        ruleId: RuleId.DAILY_GEMS_CLAIM,
        type: "on_chain",
        increase: eligibleGems,
        total: (dailyEarning?.total || 0) + eligibleGems,
        timestamp: claimTime,
      });

      // Clear any existing timeout
      clearTimeout(earningsRefreshTimeoutRef.current);

      earningsRefreshTimeoutRef.current = setTimeout(() => {
        toggleEarningsRefresh(false);
      }, HAS_JUST_EARNED_TIME_WINDOW);

      trackLegacyEvent({
        screen: "Claim",
        action: "Succeeded",
        control: "GetYourGem",
        controlType: "Button",
        userJourney: "Claim",
        extras: {
          gemBalance: newGemBalance,
          variableGemsEnabled: true,
          walletAddress,
        },
      });
    },
    [
      mutate,
      updateEarnings,
      eligibleGems,
      latestEarnings,
      toggleEarningsRefresh,
      walletAddress,
    ],
  );

  useEffect(() => {
    toggleEarningsRefresh(hasJustEarned);
  }, [hasJustEarned, toggleEarningsRefresh]);

  // Clear the timeout when the component is unmounted
  useEffect(() => {
    return () => {
      clearTimeout(earningsRefreshTimeoutRef.current);
      clearTimeout(hasJustEarnedRefreshTimeoutRef.current);
    };
  }, []);

  return (
    <VariableGems
      walletAddress={walletAddress}
      isPassportWallet={isPassportWallet}
      disconnect={disconnect}
      walletGems={walletGems}
      canClaim={canClaim}
      hasJustEarned={hasJustEarned}
      onGemEarned={onGemEarned}
      onUTCDayChange={onUTCDayChange}
      latestEarnings={latestEarnings}
      eligibleGems={eligibleGems}
    />
  );
}
